import React, { useState } from "react";
import { CheckCircle2, Fingerprint, ShieldAlert, XCircle } from "lucide-react";
import type { ProposalDecision } from "@/types/proposal";
import { dateLocale, withClock } from "@/lib/dateTime";

const fmtStamp = (d: string) =>
  new Date(d).toLocaleString(dateLocale(), withClock({ day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }));

/** The customer's decision and its evidence, for the rep (and anyone checking it later) */
export function ProposalDecisionCard({ d }: { d: ProposalDecision }) {
  const [showStatement, setShowStatement] = useState(false);
  const accepted = d.decision === "ACCEPTED";
  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex gap-3 text-xs">
      <span className="w-24 shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words">{children}</span>
    </div>
  );
  return (
    <div className="bg-card border rounded-2xl overflow-hidden">
      <div className={accepted ? "px-4 py-3 border-b bg-success/10" : "px-4 py-3 border-b bg-destructive/10"}>
        <h3 className="text-sm font-bold flex items-center gap-2">
          {accepted ? <CheckCircle2 className="size-4 text-success" /> : <XCircle className="size-4 text-destructive" />}
          {accepted ? "Accepted" : "Declined"} {d.method === "ONLINE" ? "online by the customer" : "(recorded by hand)"}
        </h3>
      </div>
      <div className="p-4 space-y-2">
        {accepted && d.method === "ONLINE" && (
          <div className="rounded-lg border bg-white p-2 h-16 flex items-center justify-center">
            {d.signature_type === "DRAWN" && d.signature_image ? (
              <img src={d.signature_image} alt={`Signature of ${d.signer_name}`} className="max-h-12" />
            ) : (
              <span style={{ fontFamily: "'Segoe Script', 'Brush Script MT', cursive" }} className="text-2xl text-primary truncate">
                {d.signer_name}
              </span>
            )}
          </div>
        )}
        <Row label={accepted ? "Accepted by" : "Declined by"}>{[d.signer_name, d.signer_title].filter(Boolean).join(", ") || "-"}</Row>
        {d.signer_email && <Row label="Email">{d.signer_email}</Row>}
        <Row label="When">{fmtStamp(d.decided_at)}</Row>
        {d.method === "ONLINE" ? (
          <>
            {accepted && <Row label="Signature">{d.signature_type === "DRAWN" ? "Drawn on screen" : "Typed name"}</Row>}
            <Row label="Through">{d.link_recipient ? `Link sent to ${d.link_recipient}` : "Customer link"}</Row>
            <Row label="IP address">{d.ip || "-"}</Row>
            <Row label="Browser">
              <span className="line-clamp-2" title={d.device || ""}>
                {d.device || "-"}
              </span>
            </Row>
          </>
        ) : (
          <Row label="Recorded by">{d.recorded_by || "-"}</Row>
        )}
        {d.reason && <Row label={accepted ? "Note" : "Reason"}>{d.reason}</Row>}
        {d.document_hash && (
          <div className={d.content_unchanged ? "flex items-start gap-1.5 text-[11px] text-muted-foreground pt-1" : "flex items-start gap-1.5 text-[11px] text-destructive pt-1"}>
            {d.content_unchanged ? <Fingerprint className="size-3.5 shrink-0 mt-px" /> : <ShieldAlert className="size-3.5 shrink-0 mt-px" />}
            <span className="break-all">
              {d.content_unchanged ? "Content matches what was sent · " : "Content no longer matches what was decided on! · "}
              SHA-256 {d.document_hash.slice(0, 16)}…
            </span>
          </div>
        )}
        {d.consent_text && (
          <div className="pt-1">
            <button type="button" className="text-xs text-primary hover:underline" onClick={() => setShowStatement((v) => !v)}>
              {showStatement ? "Hide" : "Show"} the statement they agreed to
            </button>
            {showStatement && <p className="mt-1.5 text-xs whitespace-pre-line rounded-md bg-muted/40 p-2">{d.consent_text}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
